import axios from 'axios';
import {
  CREATE_ROLE,
  UPDATE_ROLE,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { clean_string, parse_api_data } from './tenant_settings_common_helpers';
import { apply_edit_role_modal_state } from './tenant_settings_role_helpers';
import { tenant_role_collection_methods } from './tenant_settings_role_collection_methods';

export function reset_create_form() {
  this.create_form = {
    role_name: '',
    role_description: ''
  };
}

export function reset_edit_form() {
  this.edit_form = {
    role_id: null,
    role_name: '',
    role_description: ''
  };
}

export function open_create_role_modal() {
  this.reset_create_form();
  this.show_create_role_modal = true;
}

export function close_create_role_modal() {
  if (this.creating_role) {
    return;
  }

  this.show_create_role_modal = false;
  this.reset_create_form();
}

export function open_edit_role_modal(role) {
  apply_edit_role_modal_state(this, role);
}

export function close_edit_role_modal() {
  if (this.updating_role) {
    return;
  }

  this.show_edit_role_modal = false;
  this.reset_edit_form();
}

export function build_role_create_payload(component) {
  return role_payload(component.create_form);
}

export function create_role() {
  return create_role_for_component(this);
}

async function create_role_for_component(component) {
  const payload = build_role_create_payload(component);

  if (!validate_role_create_form(component, payload)) return;

  await create_role_with_loader(component, payload);
}

async function create_role_with_loader(component, payload) {
  component.creating_role = true;
  let loader = null;

  try {
    loader = await component.show_loader('Creating role...');
    const response_data = await send_role_create_request(payload);
    await apply_created_role_result(component, response_data);
  } catch (error) {
    handle_role_create_error(component, error);
  } finally {
    await reset_role_create_state(component, loader);
  }
}

function validate_role_create_form(component, payload = {}) {
  if (payload.role_name) return true;

  component.show_error('Role name cannot be empty.');
  return false;
}

async function send_role_create_request(payload) {
  const response = await axios.post(CREATE_ROLE, payload, get_request_config());
  return parse_api_data(response.data);
}

async function apply_created_role_result(component, response_data = {}) {
  show_role_create_status(component, response_data);
  component.show_create_role_modal = false;
  component.reset_create_form();
  await refresh_roles_safely(component);
  component.show_success(success_message(response_data, 'Role created successfully.'));
}

function show_role_create_status(component, response_data = {}) {
  if (!is_unsuccessful_response(response_data)) return;

  component.show_error(response_data.message || 'Role creation failed.');
}

function is_unsuccessful_response(response_data = {}) {
  return response_data?.status && String(response_data.status).toLowerCase() !== 'success';
}

function handle_role_create_error(component, error) {
  component.show_error(component.parse_error_message(error, 'Failed to create role.'));
}

async function reset_role_create_state(component, loader) {
  component.creating_role = false;
  if (loader) await component.close_loader(loader);
}

export function build_role_update_payload(component) {
  return {
    ...role_payload(component.edit_form),
    role_id: Number(component.edit_form.role_id)
  };
}

function role_payload(form = {}) {
  return {
    role_name: clean_string(form.role_name),
    role_description: clean_string(form.role_description)
  };
}

export function validate_role_update_form(component, payload) {
  if (!Number.isInteger(payload.role_id) || payload.role_id <= 0) {
    component.show_error('Role id is required.');
    return false;
  }

  if (!payload.role_name) {
    component.show_error('Role name cannot be empty.');
    return false;
  }

  return true;
}

export async function send_role_update_request(payload) {
  const response = await axios.post(UPDATE_ROLE, payload, get_request_config());
  return parse_api_data(response.data);
}

export async function apply_updated_role_result(component, response_data = {}) {
  show_role_update_status(component, response_data);
  reset_role_update_form_state(component);
  await refresh_roles_safely(component);
  component.show_success(success_message(response_data, 'Role updated successfully.'));
}

function success_message(response_data = {}, fallback = '') {
  return response_data.message || fallback;
}

async function refresh_roles_safely(component) {
  try {
    await component.refresh_roles_and_people();
  } catch (error) {
    component.roles_error = component.parse_error_message(error, 'Could not refresh configured roles.');
  }
}

function show_role_update_status(component, response_data = {}) {
  if (!is_unsuccessful_response(response_data)) return;

  component.show_error(response_data.message || 'Role update failed.');
}

function reset_role_update_form_state(component) {
  component.show_edit_role_modal = false;
  component.reset_edit_form();
}

export function handle_role_update_error(component, error) {
  component.show_error(component.parse_error_message(error, 'Failed to update role.'));
}

export async function reset_role_update_state(component, loader) {
  component.updating_role = false;
  if (loader) await component.close_loader(loader);
}

export async function update_role() {
  const payload = build_role_update_payload(this);
  if (!validate_role_update_form(this, payload)) {
    return;
  }

  this.updating_role = true;
  let loader = null;

  try {
    loader = await this.show_loader('Updating role...');
    const response_data = await send_role_update_request(payload);
    await apply_updated_role_result(this, response_data);
  } catch (error) {
    handle_role_update_error(this, error);
  } finally {
    await reset_role_update_state(this, loader);
  }
}

export const tenant_role_methods = {
  ...tenant_role_collection_methods,
  reset_create_form,
  reset_edit_form,
  open_create_role_modal,
  close_create_role_modal,
  open_edit_role_modal,
  close_edit_role_modal,
  create_role,
  update_role
};
